import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import {
  useGeofenceWatcher,
  ensureNotificationPermission,
  showSystemNotification,
} from "../lib/geofenceWatcher";
import { toast } from "sonner";
import { MapPin } from "lucide-react";

/**
 * GeofenceController — invisible component that runs the geolocation watcher
 * whenever the user has flipped on settings.geofence_alerts_enabled. Lives
 * in AppLayout so it stays mounted across route changes.
 *
 * Reacts to:
 *  - changes to /api/settings (geofence_alerts_enabled)
 *  - the custom 'flowtasks:geofence-setting-changed' event so the Settings
 *    page can toggle the watcher immediately without a settings refetch.
 */
const SETTING_EVENT = "flowtasks:geofence-setting-changed";

export default function GeofenceController() {
  const [enabled, setEnabled] = useState(false);
  const firedKeysRef = useRef(new Set()); // de-dupe events arriving via WS + ping

  // Load setting on mount
  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings")
      .then(({ data }) => !cancelled && setEnabled(!!data?.geofence_alerts_enabled))
      .catch(() => {});
    const handler = (e) => setEnabled(!!e?.detail?.enabled);
    window.addEventListener(SETTING_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(SETTING_EVENT, handler);
    };
  }, []);

  // Request notification permission once when the user enables it
  useEffect(() => {
    if (enabled) ensureNotificationPermission();
  }, [enabled]);

  const fireUserAlert = useCallback((event) => {
    const key = `${event.task_id}-${event.event}-${event.fired_at}`;
    if (firedKeysRef.current.has(key)) return;
    firedKeysRef.current.add(key);
    // Keep the set small
    if (firedKeysRef.current.size > 100) {
      firedKeysRef.current = new Set(Array.from(firedKeysRef.current).slice(-50));
    }
    const action = event.event === "arrival" ? "Arrived at" : "Left";
    const place = event.location_name || event.location_address || "your saved place";
    toast(`${action} ${place}`, {
      description: event.task_title,
      duration: 8000,
      icon: <MapPin size={14} />,
    });
    showSystemNotification(event);
  }, []);

  useGeofenceWatcher({ enabled, onEvent: fireUserAlert });

  // Also listen for WS-pushed events (in case the ping fired from another tab/device)
  useEffect(() => {
    if (!enabled) return;
    const onWs = (e) => {
      const payload = e?.detail;
      if (payload?.type === "geofence_event" && payload.event) {
        fireUserAlert(payload.event);
      }
    };
    window.addEventListener("flowtasks:ws-message", onWs);
    return () => window.removeEventListener("flowtasks:ws-message", onWs);
  }, [enabled, fireUserAlert]);

  return null;
}

/** Helper for the Settings page to toggle the watcher live. */
export function emitGeofenceSettingChange(nextValue) {
  window.dispatchEvent(
    new CustomEvent(SETTING_EVENT, { detail: { enabled: !!nextValue } })
  );
}
